export interface PositionSortHelper {
	/**
	 * Sorts an array of objects based on their numeric `position` property in ascending order.
	 *
	 * @template T - The type of the objects in the array, which must include a `position` number field.
	 * @param {T[]} items - The array of items to be sorted.
	 * @returns {T[]} - A new array sorted by the `position` property.
	 *
	 * @example
	 * const items = [{ position: 5 }, { position: 1 }, { position: 3 }];
	 * const sorted = positionBasedSortHelper(items);
	 * // sorted = [{ position: 1 }, { position: 3 }, { position: 5 }]
	 */
	<T extends { position: number }>(items: T[]): T[];

	/**
	 * Normalizes various types of values to a number for consistent position comparison.
	 * Accepts numbers, strings (dates or numeric), or Date objects.
	 *
	 * @param {number | string | Date | undefined} pos - The input value to normalize.
	 * @returns {number} - Normalized numeric representation. Returns `0` if invalid.
	 *
	 * @example
	 * positionBasedSortHelper.normalizePosition("2023-01-01"); // returns timestamp number
	 * positionBasedSortHelper.normalizePosition(123); // returns 123
	 * positionBasedSortHelper.normalizePosition(new Date()); // returns current timestamp
	 * positionBasedSortHelper.normalizePosition("invalid"); // returns 0
	 */
	normalizePosition: (pos: number | string | Date | undefined) => number;

	/**
	 * Ensures two positions are not equal by adjusting the second one if necessary.
	 *
	 * @param {number} id - First position value.
	 * @param {number} idCheck - Second position value to check for uniqueness.
	 * @returns {number} - Unique position value.
	 *
	 * @example
	 * positionBasedSortHelper.makePositionUnique(5, 5); // returns 6
	 * positionBasedSortHelper.makePositionUnique(4, 5); // returns 4
	 */
	makePositionUnique: (id: number, idCheck: number) => number;

	/**
	 * Sorts an array of objects based on their numeric `position` in descending order.
	 *
	 * @template T - The type of the objects in the array.
	 * @param {T[]} items - The array to sort.
	 * @returns {T[]} - A new array sorted descending by `position`.
	 *
	 * @example
	 * const items = [{ position: 1 }, { position: 3 }, { position: 2 }];
	 * const sortedDesc = positionBasedSortHelper.descending(items);
	 * // sortedDesc = [{ position: 3 }, { position: 2 }, { position: 1 }]
	 */
	descending: <T extends { position: number }>(items: T[]) => T[];
}

/**
 * Utility for sorting and working with models that have a numeric `position` field.
 *
 * This helper provides the following methods:
 *
 * - `positionBasedSortHelper(items)` – Sorts array of objects by ascending `position`.
 * - `positionBasedSortHelper.descending(items)` – Sorts array of objects by descending `position`.
 * - `positionBasedSortHelper.normalizePosition(value)` – Converts number, string, or date to a numeric position (timestamp or 0 fallback).
 * - `positionBasedSortHelper.makePositionUnique(a, b)` – Ensures two positions are not equal (returns `a` if different, otherwise `a + 1`).
 *
 * All functions are immutable — they return new arrays without modifying the originals.
 *
 * @example
 * import positionBasedSortHelper from './positionBasedSortHelper';
 *
 * const models = [{ position: 10 }, { position: 5 }, { position: 20 }];
 *
 * const ascending = positionBasedSortHelper(models);
 * // ascending sorted by position: [{ position: 5 }, { position: 10 }, { position: 20 }]
 *
 * const descending = positionBasedSortHelper.descending(models);
 * // descending sorted by position: [{ position: 20 }, { position: 10 }, { position: 5 }]
 *
 * const normalized = positionBasedSortHelper.normalizePosition("2025-07-10T12:00:00Z");
 * // returns a timestamp number for comparison
 *
 * const uniquePos = positionBasedSortHelper.makePositionUnique(5, 5);
 * // returns 6 to avoid duplicate positions
 */
const positionBasedSortHelper: PositionSortHelper = <
	T extends { position: number },
>(
	items: T[] = [],
): T[] => [...(items || [])].sort((a, b) => a.position - b.position);

positionBasedSortHelper.normalizePosition = (
	pos: number | string | Date | undefined,
): number => {
	if (typeof pos === "number") return pos;

	if (typeof pos === "string" || pos instanceof Date) {
		const date = new Date(pos);
		if (!Number.isNaN(date.getTime())) {
			return date.getTime();
		}
	}

	return 0;
};

positionBasedSortHelper.makePositionUnique = (
	id: number,
	idCheck: number,
): number => (id !== idCheck ? id : id + 1);

positionBasedSortHelper.descending = <T extends { position: number }>(
	items: T[],
): T[] => [...items].sort((a, b) => b.position - a.position);

export default positionBasedSortHelper;
